// Plan 06-05 Task 1 — Auth client para web-admin.
//
// Copy literal do web-comite/auth-client.ts com 2 customizações (D-138):
//   1. STORAGE_KEY: 'aions:admin:session:v1' (vs 'aions:comite:session:v1')
//   2. Role gate client-side: rejeita login se user.roles não tem admin_ti
//      nem cofre_holder — defesa em profundidade (server middleware é a fonte
//      da verdade; client é UX). Erro retornado: 'no_admin_role'.
//
// Endpoint compartilhado /api/comite/login (Open Q4 do plano) — backend
// Phase 2 aceita usuários com roles admin_ti e cofre_holder; mesmo handler
// é reutilizado.

import { deriveKeyMaterial, splitKeyMaterial } from '@aions/crypto';
import { bytesToBase64 } from './bytes';
import { clearKekAdminTi } from './kek-admin-session';
import { apiUrl } from './api-base';

const STORAGE_KEY = 'aions:admin:session:v1';

export interface SessionState {
  token: string;
  user: {
    id: string;
    email: string;
    roles: string[];
    /** Sprint 3.1 — true se aceitou versão atual LGP-02. False abre PolicyAcceptanceModal. */
    policyAccepted?: boolean;
    policyVersion?: string;
    /** Phase 2.1 D-190 — base64 de 32 bytes; salt do Argon2id KEK_admin_ti. */
    kekSalt?: string;
  };
  expInactivity?: number;
}

let memSession: SessionState | null = null;

export function getStoredSession(): SessionState | null {
  if (memSession) return memSession;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionState;
    memSession = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(s: SessionState | null): void {
  memSession = s;
  if (s) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // sessionStorage indisponível — degradar para memória apenas.
    }
  } else {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Hashes the password client-side using the same Argon2id pipeline as intake. */
async function deriveAuthHash(email: string, password: string): Promise<string> {
  const material = await deriveKeyMaterial(password, email.trim().toLowerCase());
  const { authHash } = splitKeyMaterial(material);
  return bytesToBase64(authHash);
}

export interface LoginResult {
  ok: boolean;
  error?: string;
  session?: SessionState;
}

/**
 * Role-gate client-side: bloqueia login para usuários sem admin_ti nem
 * cofre_holder. Server middleware continua sendo a fonte da verdade — este
 * gate é defesa em profundidade (UX rápido + reduz surface de tentativas
 * de acessar pages sem permissão).
 */
// 🔴 `admin_master` incluído em 19/08. Não é conveniência: o gestor único precisa alcançar
// esta SPA para ver a FILA DE PEDIDOS DE ACESSO e o cofre. Sem ele aqui, um `admin_master`
// PURO (sem acumular `admin_ti`) não passaria nem da tela de login — armadilha latente que
// só não apareceu porque a conta de teste acumula os dois papéis.
const ADMIN_ROLES = ['admin_ti', 'cofre_holder', 'admin_master'] as const;

function userHasAdminRole(roles: ReadonlyArray<string>): boolean {
  return roles.some((r) => (ADMIN_ROLES as ReadonlyArray<string>).includes(r));
}

export async function loginWithPassword(
  email: string,
  password: string,
  totpCode: string,
): Promise<LoginResult> {
  let authHashB64: string;
  try {
    authHashB64 = await deriveAuthHash(email, password);
  } catch (err) {
    void err;
    return { ok: false, error: 'network' };
  }

  let resp: Response;
  try {
    resp = await fetch(apiUrl('/api/comite/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), authHash: authHashB64, totpCode }),
    });
  } catch {
    return { ok: false, error: 'network' };
  }

  if (resp.status === 423) return { ok: false, error: 'account_locked' };
  if (!resp.ok) return { ok: false, error: 'invalid_credentials' };

  const data = (await resp.json()) as {
    token: string;
    // Phase 2.1 D-190 — salt do Argon2id KEK_admin_ti, no topo da resposta de /api/comite/login.
    kekSalt?: string;
    user: {
      id: string;
      email: string;
      roles: string[];
      policyAccepted?: boolean;
      policyVersion?: string;
    };
  };

  // D-138/D-140 — Role gate client-side (defesa em profundidade).
  if (!userHasAdminRole(data.user.roles)) {
    return { ok: false, error: 'no_admin_role' };
  }

  const session: SessionState = {
    token: data.token,
    user: { ...data.user, ...(data.kekSalt !== undefined ? { kekSalt: data.kekSalt } : {}) },
  };
  setSession(session);
  return { ok: true, session };
}

/** Sprint 3.1 — POST /policy-accept (mesmo endpoint compartilhado pelo comite/admin). */
export async function acceptPolicy(): Promise<{ ok: boolean; error?: string }> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  try {
    const resp = await fetch(apiUrl('/api/comite/policy-accept'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${s.token}` },
    });
    if (!resp.ok) return { ok: false, error: `http_${String(resp.status)}` };
    const data = (await resp.json()) as { policyVersion: string };
    setSession({
      ...s,
      user: { ...s.user, policyAccepted: true, policyVersion: data.policyVersion },
    });
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export async function logout(): Promise<void> {
  const s = getStoredSession();
  if (s) {
    try {
      await fetch(apiUrl('/api/comite/login/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${s.token}` },
      });
    } catch {
      // silently ignore — local-side cleanup ainda acontece
    }
  }
  // fix 02.8-05 (gate 9) — simetria com o comitê: zera o segredo de sessão +
  // o ciphertext da KEK_admin_ti no logout (fecha a janela de re-login na mesma aba).
  clearKekAdminTi();
  setSession(null);
}

export interface ChangePasswordResult {
  ok: boolean;
  error?: string;
}

/**
 * F3-D — troca de senha autenticada (mesmo contrato do web-comite). Deriva o
 * authHash ATUAL e o NOVO (Argon2id client-side, mesmo pipeline do login) e
 * POSTa para /api/comite/change-password (endpoint requireAuth-only: qualquer
 * papel troca a PRÓPRIA senha). Senha NUNCA trafega em claro.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  totpCode: string,
): Promise<ChangePasswordResult> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  let currentAuthHash: string;
  let newAuthHash: string;
  try {
    currentAuthHash = await deriveAuthHash(s.user.email, currentPassword);
    newAuthHash = await deriveAuthHash(s.user.email, newPassword);
  } catch {
    return { ok: false, error: 'network' };
  }
  try {
    const resp = await fetch(apiUrl('/api/comite/change-password'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${s.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ currentAuthHash, newAuthHash, totpCode }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'submit_failed' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export interface FirstAccessResult {
  ok: boolean;
  error?: string;
}

/**
 * F3-C — ativação de primeiro acesso. Deriva o authHash (Argon2id client-side) e
 * POSTa /api/comite/first-access com Bearer=firstAccessToken (JWT one-shot emitido
 * pelo admin; o endpoint é role-agnóstico). PRÉ-AUTH: usa fetch cru (não apiFetch,
 * que injeta sessão e limpa em 401). Senha NUNCA trafega em claro.
 */
export async function activateFirstAccess(
  token: string,
  email: string,
  password: string,
  totpCode: string,
): Promise<FirstAccessResult> {
  try {
    const authHash = await deriveAuthHash(email, password);
    const resp = await fetch(apiUrl('/api/comite/first-access'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authHash, totpCode }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'generic' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

// =====================================================================
// ADR-0009 Peça 6 — liveness no login do ADMIN (24/08)
// =====================================================================
//
// O web-admin não tinha biometria: login era e-mail + senha + TOTP e pronto. Passou a ter
// porque `admin_ti` é quem gere contas, cadastra rosto alheio e destrava lockout — poder
// alto o bastante para merecer o mesmo terceiro fator do comitê.
//
// TRANSPORTE aqui, TRADUÇÃO no `@aions/ui-primitives`. O contrato de wire (status → outcome)
// é cópia única compartilhada com o comitê: um status novo no servidor se conserta em um
// lugar só. Ver `liveness-contract.ts` para o porquê.
//
// ⚠️ O path é `/api/comite/liveness/*` mesmo para o admin. Não é descuido: é UMA máquina de
// liveness, com um lockout e um contador por usuário. O guard de papel na API foi estendido
// para aceitar `admin_ti`; montar uma segunda superfície em `/api/admin/liveness` daria dois
// contadores independentes para a mesma pessoa.
import {
  parseChallengeResponse,
  parseVerifyResponse,
  erroDeTransporte,
  type ChallengeResult,
  type LivenessOutcome,
} from '@aions/ui-primitives';
import { apiFetch } from './api-client';

export type { ChallengeResult, LivenessOutcome };

/** POST /api/comite/liveness/challenge — sorteia direção + token single-use. */
export async function drawLivenessChallenge(): Promise<ChallengeResult> {
  try {
    return await parseChallengeResponse(
      await apiFetch('/api/comite/liveness/challenge', { method: 'POST' }),
    );
  } catch {
    return { ok: false, error: 'network' };
  }
}

/** POST /api/comite/liveness/verify — multipart vídeo + challengeToken. */
export async function verifyLiveness(
  challengeToken: string,
  video: Blob,
  signal?: AbortSignal,
): Promise<LivenessOutcome> {
  const fd = new FormData();
  fd.append('video', video, 'verificacao.webm');
  fd.append('challengeToken', challengeToken);

  let resp: Response;
  try {
    resp = await apiFetch('/api/comite/liveness/verify', {
      method: 'POST',
      body: fd,
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    return erroDeTransporte(e);
  }

  // O `apiFetch` do admin já aplica `X-Session-Refresh`; o body é o reforço determinístico.
  return parseVerifyResponse(resp, (token) => {
    const atual = getStoredSession();
    if (atual) setSession({ ...atual, token });
  });
}
